import React, { useCallback, useEffect, useRef, useState } from 'react';

const IDLE_MS = 3800;
/** Abaixo deste restante o chrome fica sempre visível (alerta). */
export const CAB_CHROME_URGENT_SECONDS = 60;

const CHROME_POS_STORAGE_KEY = 'suportnect.cabPresentChromePos.v1';

export type CabChromePos = { xPct: number; yPct: number };

export function formatCabClock(total: number): string {
  const safe = Math.max(0, Math.floor(total));
  const hours = Math.floor(safe / 3600);
  const minutes = String(Math.floor((safe % 3600) / 60)).padStart(2, '0');
  const seconds = String(safe % 60).padStart(2, '0');
  if (hours > 0) return `${hours}:${minutes}:${seconds}`;
  return `${minutes}:${seconds}`;
}

export function shouldChromeStayVisible(opts: {
  pinned: boolean;
  remainingSeconds: number;
  ghost: boolean;
}): boolean {
  if (opts.pinned) return true;
  if (opts.remainingSeconds <= CAB_CHROME_URGENT_SECONDS) return true;
  return !opts.ghost;
}

/** Mantém o bloco dentro do slide (caixa fixa ~188px). */
export function clampChromePos(pos: CabChromePos): CabChromePos {
  return {
    xPct: Math.max(0, Math.min(88, Number.isFinite(pos.xPct) ? pos.xPct : 0)),
    yPct: Math.max(0, Math.min(86, Number.isFinite(pos.yPct) ? pos.yPct : 0)),
  };
}

export function loadCabChromePos(): CabChromePos | null {
  try {
    const raw = window.sessionStorage.getItem(CHROME_POS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CabChromePos;
    if (typeof parsed?.xPct !== 'number' || typeof parsed?.yPct !== 'number') return null;
    return clampChromePos(parsed);
  } catch {
    return null;
  }
}

export function saveCabChromePos(pos: CabChromePos | null): void {
  try {
    if (!pos) {
      window.sessionStorage.removeItem(CHROME_POS_STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(CHROME_POS_STORAGE_KEY, JSON.stringify(clampChromePos(pos)));
  } catch {
    /* ignore */
  }
}

/**
 * Cronômetro do slide: arrastável, some após idle (fantasma),
 * volta com o mouse; pode ser fixado; urgente se resto ≤ 60s.
 */
export function CabStageChrome({
  remaining,
  countdown,
  elapsed,
  totalElapsed,
  slideLabel,
  activityNonce = 0,
}: {
  remaining: number;
  countdown: number;
  elapsed: number;
  totalElapsed: number;
  slideLabel?: string;
  /** Incrementar quando o stage detectar mouse/tecla — restaura o chrome. */
  activityNonce?: number;
}) {
  const [ghost, setGhost] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState<CabChromePos | null>(() => loadCabChromePos());
  const [dragging, setDragging] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const dragRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    origXPct: number;
    origYPct: number;
    parentW: number;
    parentH: number;
    moved: boolean;
  } | null>(null);
  const urgent = remaining <= CAB_CHROME_URGENT_SECONDS;

  const clearIdle = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleGhost = useCallback(() => {
    clearIdle();
    if (pinned || remaining <= CAB_CHROME_URGENT_SECONDS || dragRef.current) {
      setGhost(false);
      return;
    }
    timerRef.current = window.setTimeout(() => setGhost(true), IDLE_MS);
  }, [clearIdle, pinned, remaining]);

  const reveal = useCallback(() => {
    setGhost(false);
    scheduleGhost();
  }, [scheduleGhost]);

  useEffect(() => {
    if (activityNonce === 0) return undefined;
    reveal();
    return clearIdle;
  }, [activityNonce, reveal, clearIdle]);

  useEffect(() => {
    if (urgent || pinned || dragging) {
      setGhost(false);
      clearIdle();
      return undefined;
    }
    scheduleGhost();
    return clearIdle;
  }, [urgent, pinned, dragging, scheduleGhost, clearIdle]);

  const resolveDefaultPct = useCallback((): CabChromePos => {
    const el = rootRef.current;
    const parent = el?.offsetParent as HTMLElement | null;
    if (!el || !parent) return { xPct: 72, yPct: 2 };
    const p = parent.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    return clampChromePos({
      xPct: ((r.left - p.left) / Math.max(1, p.width)) * 100,
      yPct: ((r.top - p.top) / Math.max(1, p.height)) * 100,
    });
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) return;
    if (e.button !== 0) return;
    const el = rootRef.current;
    const parent = el?.offsetParent as HTMLElement | null;
    if (!el || !parent) return;
    e.preventDefault();
    e.stopPropagation();
    reveal();
    const p = parent.getBoundingClientRect();
    const current = pos || resolveDefaultPct();
    dragRef.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      origXPct: current.xPct,
      origYPct: current.yPct,
      parentW: Math.max(1, p.width),
      parentH: Math.max(1, p.height),
      moved: false,
    };
    setDragging(true);
    el.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const dxPct = ((e.clientX - drag.startClientX) / drag.parentW) * 100;
    const dyPct = ((e.clientY - drag.startClientY) / drag.parentH) * 100;
    if (!drag.moved && Math.abs(dxPct) + Math.abs(dyPct) < 0.5) return;
    drag.moved = true;
    setPos(
      clampChromePos({
        xPct: drag.origXPct + dxPct,
        yPct: drag.origYPct + dyPct,
      }),
    );
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const didMove = drag.moved;
    dragRef.current = null;
    setDragging(false);
    try {
      rootRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    if (didMove) {
      setPos((prev) => {
        if (!prev) return prev;
        const next = clampChromePos(prev);
        saveCabChromePos(next);
        return next;
      });
    }
    scheduleGhost();
  };

  const resetPosition = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPos(null);
    saveCabChromePos(null);
    reveal();
  };

  const visible = shouldChromeStayVisible({
    pinned,
    remainingSeconds: remaining,
    ghost: dragging ? false : ghost,
  });

  const style: React.CSSProperties = pos
    ? { left: `${pos.xPct}%`, top: `${pos.yPct}%`, right: 'auto' }
    : {};

  return (
    <div
      ref={rootRef}
      className={[
        'cab-cco-chrome',
        visible ? '' : 'is-ghost',
        urgent ? 'is-urgent' : '',
        pinned ? 'is-pinned' : '',
        dragging ? 'is-dragging' : '',
        pos ? 'is-custom-pos' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={style}
      role="timer"
      aria-live={urgent ? 'polite' : 'off'}
      aria-label={`Tempo restante ${formatCabClock(remaining)} de ${formatCabClock(countdown)}. Arraste para reposicionar; duplo clique restaura.`}
      title="Arraste para mover · duplo clique restaura o canto"
      onMouseEnter={reveal}
      onFocus={reveal}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={resetPosition}
    >
      <div className="cab-cco-clock">{formatCabClock(remaining)}</div>
      <div>de {formatCabClock(countdown)}</div>
      <div className="cab-cco-chrome-meta">
        <span>
          nesta mudança {formatCabClock(elapsed)} · reunião {formatCabClock(totalElapsed)}
        </span>
        {slideLabel ? <span className="cab-cco-chrome-slide">{slideLabel}</span> : null}
      </div>
      <div className="cab-cco-chrome-actions">
        <button
          type="button"
          className="cab-cco-chrome-pin"
          aria-pressed={pinned}
          title={
            pinned
              ? 'Cronômetro fixo — clique para ocultar automaticamente'
              : 'Fixar cronômetro — permanece sempre visível'
          }
          onClick={(e) => {
            e.stopPropagation();
            setPinned((prev) => !prev);
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {pinned ? 'Fixo' : 'Auto'}
        </button>
        {pos ? (
          <button
            type="button"
            className="cab-cco-chrome-pin"
            title="Voltar ao canto superior direito"
            onClick={resetPosition}
            onPointerDown={(e) => e.stopPropagation()}
          >
            Canto
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default CabStageChrome;
